import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Trash2, Sparkles, AlertTriangle, Play,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

export default function GanapathiSettings() {
  const navigate = useNavigate();
  const [state, setState]           = useState(null);
  const [personaText, setPersonaText] = useState('');
  const [defaultPersona, setDefaultPersona] = useState('');
  const [directives, setDirectives] = useState([]);
  const [newDirective, setNewDirective] = useState('');
  const [conflict, setConflict]     = useState(null);
  const [pendingText, setPendingText] = useState('');
  const [dossiers, setDossiers]     = useState([]);
  const [salesmen, setSalesmen]     = useState([]);
  const [selectedSm, setSelectedSm] = useState('');
  const [savingPersona, setSavingPersona] = useState(false);
  const [addingDirective, setAddingDirective] = useState(false);
  const [buildingDossier, setBuildingDossier] = useState(false);

  const load = async () => {
    try {
      const [s, p, d, dos, sm] = await Promise.all([
        api.get('/ganapathi/state'),
        api.get('/ganapathi/persona'),
        api.get('/ganapathi/directives'),
        api.get('/ganapathi/dossier'),
        api.get('/users/by-role/salesman'),
      ]);
      setState(s.data);
      setPersonaText(p.data.text);
      setDefaultPersona(p.data.default);
      setDirectives(d.data || []);
      setDossiers(dos.data || []);
      setSalesmen(sm.data || []);
    } catch (e) {
      toast({ title: 'Failed to load', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };
  useEffect(() => { load(); }, []);

  const savePersona = async () => {
    setSavingPersona(true);
    try {
      await api.put('/ganapathi/persona', { text: personaText });
      toast({ title: '✅ Persona saved' });
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setSavingPersona(false); }
  };

  const resetPersona = () => {
    if (window.confirm('Reset persona to default?')) setPersonaText(defaultPersona);
  };

  const addDirective = async (force = false, supersede_id = null) => {
    const text = force ? pendingText : newDirective.trim();
    if (!text) { toast({ title: 'Type a directive first' }); return; }
    setAddingDirective(true);
    try {
      const r = await api.post('/ganapathi/directives', { text, force, supersede_id });
      if (r.data.needs_resolution) {
        setConflict(r.data.conflict);
        setPendingText(text);
        toast({ title: '⚠️ Conflict detected — resolve below', variant: 'destructive' });
      } else {
        setNewDirective('');
        setConflict(null);
        setPendingText('');
        toast({ title: '✅ Directive saved' });
        load();
      }
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setAddingDirective(false); }
  };

  const removeDirective = async (id) => {
    if (!window.confirm('Delete this directive?')) return;
    try {
      await api.delete(`/ganapathi/directives/${id}`);
      toast({ title: 'Deleted' });
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
  };

  const buildDossier = async () => {
    if (!selectedSm) { toast({ title: 'Select a salesman first' }); return; }
    setBuildingDossier(true);
    try {
      const r = await api.post(`/ganapathi/dossier/${selectedSm}/generate`);
      if (r.data.success) toast({ title: '✅ Dossier built' });
      else toast({ title: r.data.reason || 'Not built', variant: 'destructive' });
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBuildingDossier(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-emerald-700 p-2" data-testid="gp-settings-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate flex items-center gap-2">
              <Sparkles className="w-5 h-5" /> Ganapathi Settings
            </h1>
            <p className="text-xs text-emerald-100">Mode: {state?.mode?.toUpperCase() || '—'}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-3 space-y-4">
        {/* Directives */}
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-gray-800">Active Owner Directives</h2>
            <Badge variant="outline" className="text-xs">{directives.filter(d => d.status === 'active').length}</Badge>
          </div>
          <p className="text-xs text-gray-500">Rules Ganapathi will always honour. Conflicts are auto-detected.</p>

          {directives.filter(d => d.status === 'active').map(d => (
            <div key={d.id} className="border border-gray-200 rounded-xl p-2 flex items-start gap-2" data-testid={`gp-directive-${d.id}`}>
              <p className="flex-1 text-sm text-gray-700 whitespace-pre-wrap">{d.text}</p>
              <Button size="sm" variant="ghost" onClick={() => removeDirective(d.id)} className="text-red-500" data-testid={`gp-remove-dir-${d.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {directives.filter(d => d.status === 'active').length === 0 && (
            <p className="text-xs text-gray-400 text-center py-3">No active directives yet.</p>
          )}

          <div className="border-t pt-3 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase">Add new directive</p>
            <textarea
              value={newDirective}
              onChange={e => setNewDirective(e.target.value)}
              rows={2}
              placeholder='e.g. "Always prefer ready stock first, then customisation"'
              className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="gp-new-directive-input"
            />

            {conflict ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-amber-800">Conflict with an existing directive</p>
                    <p className="text-xs text-amber-700 mt-1">&quot;{conflict.existing_text}&quot;</p>
                    <p className="text-[11px] text-amber-600 mt-1"><em>Reason: {conflict.reason}</em></p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => { setConflict(null); setPendingText(''); }} data-testid="gp-conflict-cancel">
                    Cancel
                  </Button>
                  <Button size="sm" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => addDirective(true, conflict.conflicting_id)} data-testid="gp-conflict-override">
                    Override old rule
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1"
                    onClick={() => addDirective(true, null)} data-testid="gp-conflict-coexist">
                    Keep both
                  </Button>
                </div>
              </div>
            ) : (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => addDirective(false)} disabled={addingDirective} data-testid="gp-add-directive-btn">
                {addingDirective ? 'Checking…' : 'Add directive'}
              </Button>
            )}
          </div>
        </div>

        {/* Persona */}
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-bold text-gray-800">Persona Prompt</h2>
          <p className="text-xs text-gray-500">The full system prompt that defines Ganapathi. Edit carefully.</p>
          <textarea
            value={personaText}
            onChange={e => setPersonaText(e.target.value)}
            rows={14}
            className="w-full border border-gray-200 rounded-lg p-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400"
            data-testid="gp-persona-textarea"
          />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={resetPersona} data-testid="gp-reset-persona">
              Reset to default
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
              onClick={savePersona} disabled={savingPersona} data-testid="gp-save-persona">
              <Save className="w-4 h-4 mr-1" /> {savingPersona ? 'Saving…' : 'Save persona'}
            </Button>
          </div>
        </div>

        {/* Dossiers */}
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-bold text-gray-800">Salesman Dossiers (HR-view)</h2>
          <p className="text-xs text-gray-500">Silent observations, owner-only. Not visible to any salesman.</p>

          <div className="flex gap-2">
            <select
              value={selectedSm}
              onChange={e => setSelectedSm(e.target.value)}
              className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
              data-testid="gp-dossier-sm-select"
            >
              <option value="">-- Select salesman --</option>
              {salesmen.map(s => <option key={s.id} value={s.id}>{s.username}</option>)}
            </select>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={buildDossier} disabled={buildingDossier} data-testid="gp-build-dossier">
              <Play className="w-4 h-4 mr-1" /> {buildingDossier ? 'Building…' : 'Build'}
            </Button>
          </div>

          {dossiers.map(dos => (
            <div key={dos.salesman_id} className="border border-gray-200 rounded-xl p-3 space-y-2" data-testid={`gp-dossier-${dos.salesman_id}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm text-gray-800">{dos.salesman_name}</p>
                <Badge className={
                  dos.trajectory === 'improving' ? 'bg-emerald-100 text-emerald-700' :
                  dos.trajectory === 'declining' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }>{dos.trajectory}</Badge>
              </div>
              {dos.last_note && <p className="text-xs text-gray-600 italic">&quot;{dos.last_note}&quot;</p>}
              {dos.strengths?.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-emerald-700 uppercase">Strengths</p>
                  <ul className="text-xs text-gray-700 list-disc pl-4">
                    {dos.strengths.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {dos.weaknesses?.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-red-700 uppercase">Weaknesses</p>
                  <ul className="text-xs text-gray-700 list-disc pl-4">
                    {dos.weaknesses.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {dos.behaviours?.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-blue-700 uppercase">Behaviours</p>
                  <ul className="text-xs text-gray-700 list-disc pl-4">
                    {dos.behaviours.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ))}
          {dossiers.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-3">No dossiers built yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
